/**
 * Formats stepped checkout form data for GraphQL ORDER_SUBMIT_MUTATION
 *
 * @param {Object} purchaseFormData - Form data from Purchase Form Context
 * @param {Object} checkoutContext - Checkout page context data
 * @returns {Object} Formatted data for orderSubmitInput GraphQL mutation
 */
export default function formatSteppedCheckoutData(purchaseFormData, checkoutContext = {}) {
  const {
    addresses,
    billingAddressSameAsShipping,
    smsOptIn,
    shipModeId,
    email,
    phone,
  } = purchaseFormData;

  const {
    bagItems,
    shipMethod,
    shippingAddress,
    selectedStoreEdd,
  } = checkoutContext;

  // Get contact information
  const contactEmail = email || '';
  const contactPhone = phone || '';

  // Helper to update addresses with contact info
  const updateAddressContactInfo = (address) => {
    if (!address) return null;

    // Remove GraphQL metadata and invalid fields
    const {
      __typename,
      email: addressEmail,
      phone: addressPhone,
      poBox,
      displayAddress,
      ...cleanAddress
    } = address;

    // Populate email/phone and convert poBox to boolean
    return {
      ...cleanAddress,
      email: contactEmail,
      phone: contactPhone,
      poBox: typeof poBox === 'boolean' ? poBox : !!poBox,
    };
  };
  const pickupType = shipMethod?.shipModeType?.toLowerCase();
  const deliveryType = shipMethod?.isHomeDelivery ? 'homeDelivery' : pickupType;
  const { addressId = '', addressType = '' } = shippingAddress || {};
  const hasOnlyEGiftCard = bagItems?.hasOnlyEGiftCard;

  // Transform the nested structure to match CRS format
  const transformedData = {
    // Shipping address - use as-is with contact info
    shippingAddress: updateAddressContactInfo(
      {
        ...addresses?.[deliveryType],
        addressId,
        addressType,
      },
    ),

    // Billing address - only include if NOT same as shipping
    ...(billingAddressSameAsShipping ? {} : {
      billingAddress: updateAddressContactInfo(addresses?.billing),
    }),
    ...(hasOnlyEGiftCard ? { shippingAddressSameAsBilling: true } : {}),

    // Payment information
    payment: {
      cardPayments: checkoutContext?.paymentInfo?.cardPayments
        || purchaseFormData?.creditCard || [],
    },

    // Include items from bag context - use existing structure
    items: bagItems?.items?.map((bagItem, index) => {
      const { item } = bagItem;
      const productContent = item?.productContent;
      const shortSku = productContent?.shortSku || '';
      const shipmentItem = selectedStoreEdd?.items?.[index] || {};
      return {
        estimatedDeliveryDate: shipmentItem?.edd || '',
        estimatedShipDate: shipmentItem?.esd || '',
        isPreOrderItem: shipmentItem?.preorder || 'false',
        shortSku,
        brand: productContent?.brand || '',
      };
    }) || [],

    // Include shipping method information
    shipModeId: shipModeId || shipMethod?.shipModeId || '',

    // Simple flags
    billingAddressSameAsShipping: billingAddressSameAsShipping || false,
    smsOptIn: smsOptIn || false,
  };

  return transformedData;
}
